import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import Navbar from './components/Navbar';
import ProductCard from './components/ProductCard';
import Footer from './components/Footer';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import ProtectedRoute from './components/ProtectedRoute';

// Import Application Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ChangePassword from './pages/ChangePassword';
import UserDashboard from './pages/UserDashboard';
import About from './pages/About';
import PartnerWithUs from './pages/PartnerWithUs';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AddProduct from './pages/AddProduct';
import LogisticsSettings from './pages/LogisticsSettings';
import NotFound from './pages/NotFound';
import Checkout from './pages/Checkout';
import OrderTracking from './pages/OrderTracking';
import VerifyEmail from './pages/VerifyEmail';
import { recordVisit, fetchProducts } from './pages';
import { Product } from './types';
import ContactForm from './pages/ContactForm';
import Spinner from './components/Spinner';

// Create a simple Home component for the landing page content
const Home = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [originFilter, setOriginFilter] = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12; // A good standard for a 3-column grid
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const { data } = await fetchProducts();
        // Map _id to id for frontend compatibility
        setProducts(data.map((p: any) => ({ ...p, id: p._id })));
      } catch (error) {
        console.error("Failed to fetch products", error);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesOrigin = originFilter === 'All' || product.origin === originFilter;
    const matchesCategory = categoryFilter === 'All' || product.category === categoryFilter;
    return matchesSearch && matchesOrigin && matchesCategory;
  });

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  return (
    <>
      <Navbar />
      <main className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-12 items-start">
          {/* Left Sidebar - Quick Access */}
          <aside className="w-full lg:w-72 space-y-6 lg:sticky lg:top-24 order-2 lg:order-1 flex-shrink-0">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 text-center">
              <h3 className="text-xl font-serif font-bold text-gray-800 mb-2">Track Your Order</h3>
              <p className="text-sm text-gray-600 mb-4 font-sans">
                Waiting for a delivery? Check the real-time status.
              </p>
              <Link to="/track-order" className="inline-block text-primary font-bold hover:text-green-800 border-b-2 border-primary pb-1 transition text-sm">
                Track Order &rarr;
              </Link>
            </div>

            <div className="bg-primary p-6 rounded-lg shadow-sm text-center">
              <h3 className="text-xl font-serif font-bold text-white mb-2">Partner With Us</h3>
              <p className="text-sm text-green-100 mb-4 font-sans">
                Supermarket or Hotel? Get wholesale prices.
              </p>
              <Link to="/partner-with-us" className="inline-block bg-white text-primary px-4 py-2 rounded-md font-bold hover:bg-gray-100 transition text-sm">
                Become a Partner
              </Link>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 text-center">
              <h3 className="text-xl font-serif font-bold text-gray-800 mb-2">Need Help?</h3>
              <p className="text-sm text-gray-600 mb-4 font-sans">
                Have questions? Send us a message.
              </p>
              <Link to="/contact" className="inline-block text-primary font-bold hover:text-green-800 border-b-2 border-primary pb-1 transition text-sm">
                Contact Us &rarr;
              </Link>
            </div>
          </aside>

          {/* Main Content */}
          <div className="w-full flex-1 order-1 lg:order-2">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-serif font-bold text-primary mb-4">Premium Fresh Produce</h2>
              <p className="text-gray-600 font-sans max-w-2xl mx-auto mb-8">
                Sourcing high-grade local fruits and vegetables, alongside premium imported F1 produce from the Netherlands, South Africa, and beyond.
              </p>
              <div className="flex justify-center gap-4">
                <Link to="/register" className="bg-primary text-white px-6 py-3 rounded-md font-medium hover:bg-green-800 transition">
                  Get Started
                </Link>
                <Link to="/login" className="bg-white text-primary border border-primary px-6 py-3 rounded-md font-medium hover:bg-gray-50 transition">
                  Sign In
                </Link>
              </div>
            </div>

            {/* Search & Filter Section */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 mb-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Search */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 font-sans">Search Produce</label>
                  <input 
                    type="text" 
                    placeholder="Search by name..." 
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-primary focus:border-primary font-sans"
                  />
                </div>
                {/* Origin Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 font-sans">Origin</label>
                  <select 
                    value={originFilter}
                    onChange={(e) => { setOriginFilter(e.target.value); setCurrentPage(1); }}
                    className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-primary focus:border-primary font-sans"
                  >
                    <option value="All">All Origins</option>
                    <option value="Local">Local</option>
                    <option value="Imported">Imported</option>
                  </select>
                </div>
                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 font-sans">Category</label>
                  <select 
                    value={categoryFilter}
                    onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
                    className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-primary focus:border-primary font-sans"
                  >
                    <option value="All">All Categories</option>
                    <option value="Vegetables">Vegetables</option>
                    <option value="Fruits">Fruits</option>
                    <option value="Tubers">Tubers</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
              {loading ? (
                <div className="col-span-full text-center py-12 flex flex-col items-center justify-center">
                  <Spinner className="h-10 w-10 text-primary mb-4" />
                  <p className="text-gray-500 font-sans">Loading fresh produce...</p>
                </div>
              ) : currentItems.length > 0 ? (
                currentItems.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-500 font-sans text-lg">No products found matching your search.</p>
                  <button 
                    onClick={() => {setSearchTerm(''); setOriginFilter('All'); setCategoryFilter('All'); setCurrentPage(1);}}
                    className="mt-4 text-primary hover:text-accent font-medium"
                  >
                    Clear Filters
                  </button>
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            {filteredProducts.length > itemsPerPage && (
              <div className="mt-12 flex justify-center items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 border rounded-md font-sans text-sm ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-primary hover:bg-gray-50 border-primary'}`}
                >
                  Previous
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => handlePageChange(i + 1)}
                    className={`px-4 py-2 border rounded-md font-sans text-sm ${currentPage === i + 1 ? 'bg-primary text-white border-primary' : 'bg-white text-primary hover:bg-gray-50 border-primary'}`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 border rounded-md font-sans text-sm ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-primary hover:bg-gray-50 border-primary'}`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

function App() {
  useEffect(() => {
    // Record a visit when the app loads
    recordVisit().catch(err => console.error("Analytics failed", err));
  }, []);

  return (
    <AuthProvider>
      <CartProvider>
        <Router>
          <div className="min-h-screen bg-secondary flex flex-col">
            <div className="flex-grow">
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="/verify/:token" element={<VerifyEmail />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<ContactForm />} />
                <Route path="/partner-with-us" element={<PartnerWithUs />} />

                {/* Protected Routes */}
                <Route element={<ProtectedRoute allowedRoles={['Customer', 'Admin', 'Super-Admin']} />}>
                  <Route path="/userdashboard" element={<UserDashboard />} />
                </Route>

                <Route element={<ProtectedRoute allowedRoles={['Super-Admin']} />}>
                  <Route path="/super-admin" element={<SuperAdminDashboard />} />
                  <Route path="/super-admin/logistics" element={<LogisticsSettings />} />
                </Route>

                <Route element={<ProtectedRoute allowedRoles={['Admin', 'Super-Admin']} />}>
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/add-product" element={<AddProduct />} />
                  <Route path="/admin/edit-product/:id" element={<AddProduct />} />
                </Route>

                {/* Public Checkout (Guest Allowed) */}
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/track-order" element={<OrderTracking />} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
            <Footer />
          </div>
        </Router>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;